import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-900 text-white pb-24 lg:pb-0">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-700">
        <div className="container-custom py-8 md:py-10 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-white">Готовы к путешествию?</h3>
            <p className="text-white/70 text-sm mt-1">Оставьте заявку — подберем идеальный тур</p>
          </div>
          <div className="flex gap-3">
            <a href="#contacts" className="btn bg-white text-primary-600 hover:bg-white/90 font-bold text-sm py-2.5 px-5">
              Оставить заявку
            </a>
            <a href="https://wa.me/77019823126" target="_blank" rel="noopener noreferrer" className="btn bg-green-500 text-white hover:bg-green-600 text-sm py-2.5 px-5">
              <MessageCircle className="w-4 h-4 mr-1.5" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Company Info — wider */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center font-bold text-sm">HT</div>
              <span className="text-lg font-bold">HASAN TRAVEL</span>
            </div>
            <p className="text-neutral-400 text-sm leading-relaxed mb-5 max-w-sm">
              Ваши надежные партнеры в туризме с 2015 года. Организуем незабываемый отдых в любой точке мира.
            </p>
            <div className="space-y-2 text-sm mb-5">
              <a href="tel:+77019823126" className="flex items-center gap-2.5 text-neutral-300 hover:text-secondary-300 transition-colors">
                <Phone className="w-4 h-4 text-secondary-300/70" />+7 701 982 31 26
              </a>
              <a href="mailto:info@hasantravel.kz" className="flex items-center gap-2.5 text-neutral-300 hover:text-secondary-300 transition-colors">
                <Mail className="w-4 h-4 text-secondary-300/70" />info@hasantravel.kz
              </a>
              <div className="flex gap-2.5 text-neutral-400">
                <MapPin className="w-4 h-4 text-secondary-300/70 flex-shrink-0 mt-0.5" />
                <span>г. Павлодар, ул. Исы Байзакова, 41, 3 этаж</span>
              </div>
            </div>
            {/* Social */}
            <div className="flex gap-2">
              {[
                { label: 'Instagram', svg: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
                { label: 'WhatsApp', icon: <MessageCircle className="w-4 h-4" />, href: 'https://wa.me/77019823126' },
                { label: 'Telegram', icon: <Send className="w-4 h-4" />, href: 'https://t.me/hasantravel' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href || '#'}
                  target={social.href ? '_blank' : undefined}
                  rel={social.href ? 'noopener noreferrer' : undefined}
                  className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors"
                  aria-label={social.label}
                >
                  {social.svg || social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold mb-4 uppercase tracking-widest text-neutral-500">Направления</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {['Турция', 'Египет', 'ОАЭ', 'Таиланд', 'Мальдивы', 'Греция', 'Испания', 'Италия'].map((country) => (
                <a key={country} href="#tours" className="text-neutral-400 hover:text-white transition-colors text-sm py-0.5">
                  {country}
                </a>
              ))}
            </div>
          </div>

          {/* Info & Tours */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold mb-4 uppercase tracking-widest text-neutral-500">Услуги</h3>
            <div className="space-y-1.5">
              {['Пляжный отдых', 'Экскурсии', 'Горнолыжные', 'Круизы', 'VIP туры'].map((type) => (
                <a key={type} href="#" className="block text-neutral-400 hover:text-white transition-colors text-sm py-0.5">
                  {type}
                </a>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold mb-4 uppercase tracking-widest text-neutral-500">Инфо</h3>
            <div className="space-y-1.5">
              {[
                { href: '#about', label: 'О компании' },
                { href: '#', label: 'Оплата' },
                { href: '#', label: 'FAQ' },
                { href: '#', label: 'Политика' },
                { href: '#contacts', label: 'Контакты' },
              ].map((item) => (
                <a key={item.label} href={item.href} className="block text-neutral-400 hover:text-white transition-colors text-sm py-0.5">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-neutral-500 text-xs">&copy; {currentYear} ТОО "Hasan Tour". Все права защищены.</span>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              {['VISA', 'MC', 'KASPI'].map((pay) => (
                <div key={pay} className="px-2 py-1 bg-white/5 rounded text-[10px] font-bold text-neutral-400">{pay}</div>
              ))}
            </div>
            <span className="text-neutral-700">|</span>
            <a href="https://info-service.kz" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-neutral-400 text-xs transition-colors">
              ИНФО-СЕРВИС
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
